<template>
  <div>
    <h1>Frequently Asked Questions</h1>

    <h2>What can a team work on?</h2>
    <p><strong>Anything your heart desires!</strong> (as long as it involves code).</p>

    <p>
      It does not have to have "business value" or be work related.
      Contribute to open source! Make a game! Create that personal tool you've been thinking
      about.
    </p>

    <p>
      The only criteria is that it has to involve <strong>some amount of coding</strong>.
      This is a hackathon, not an idea generator: you don't have to <i>finish</i> your idea,
      but you do need to have some "product" to present on at the end.
    </p>

    <h2>How many people can be on a team?</h2>
    <p>
      Check the event page! It will say "The max team size is" with the number.
      Unless it's been overridden, the max size is four people per team.
      There is no minimum team size.
    </p>

    <h2>Can I change teams?</h2>
    <p>
      Yes, as long as the event is in <strong>Registration</strong> or <strong>Hacking</strong>.
      <i>New</i> teams can only be created in <strong>Registration</strong>: once hacking starts,
      you can only join an existing team.
    </p>

    <h2>How do I delete my team?</h2>
    <p>
      The team leader will have the option to "Disband Team".
      Alternately, when the last team member leaves, a team will be automatically disbanded.
    </p>

    <h2>How long can I spend on my project?</h2>
    <p>
      You have <strong>24 hours</strong> during the <strong>Hacking</strong> state. This
      could be three 8-hour days, or you could do two marathon 12-hour days, or work around
      your schedule as-needed. You could time shift to work the same hours as a teammate across the
      world! This is supposed to be fun, not a hardship on you or your family.
    </p>

    <p>
      Importantly, though: <i>creating your presentation must be done within those 24 hours</i>!
    </p>

    <h2>What if I worked on my project some already?</h2>
    <p>
      That's okay! Just clearly explain what you did before the event and what you did during it.
    </p>

    <h2>Can I 3D print something and leave it running overnight, or does that "count"?</h2>
    <p>That's okay! Robots that do your bidding are exempt from time restraints.</p>

    <h2>How are prizes given out? Is there a first place prize, etc?</h2>
    <p>
      There are no set prizes. Instead, we have a list of available prizes and each
      participant orders them however they'd like: maybe you don't care about 3D printing,
      or have a printer already: you might rank that option low but a Steam Deck high, whereas
      someone who doesn't play games but wants to print things would do the opposite!
    </p>

    <p>
      If your team wins a prize, you'll receive the top prize on your list
      that hasn't been claimed.
    </p>

    <h2>Does the order of my team's members matter?</h2>
    <p>
      Yes! If your team wins prizes, its members will have prizes selected in order.
      So if there are two iPads available and all four members have it as their top prize,
      the first and second will get them.
    </p>

    <p>
      The team leader does not have to be first.
    </p>

    <h2>What happens if I don't order my prize list? (Why do I have to order my prize list?)</h2>
    <p>
      Your prize list is randomized, so you'll get whatever is chosen and not what you might
      actually want.
    </p>

    <p>
      But: you can take five minutes and put it in order. Come on. (in the future you may
      instead <i>not win anything</i>!)
    </p>

    <h2>What if there are fewer prizes remaining than members of a team?</h2>
    <p>
      That is: if there are three unclaimed prizes and the next team has four members, what
      happens?
    </p>

    <p>
      Either <strong>everyone</strong> on a team wins a prize or <strong>no one</strong> does.
      Any remaining prizes are <strong>randomly assigned</strong> to people who didn't win.
    </p>

    <p>
      We understand that this might be confusing! We're considering changing it,
      but it's how it works right now.
    </p>

    <h2>How does voting work?</h2>
    <p>
      Teams create a video and host it somewhere (Google docs? Private YouTube! Do as thou wilt!)
      During <strong>Judging</strong> participants will watch the videos and vote.
    </p>

    <h2>How do I vote if I didn't participate?</h2>
    <p>That's the neat part, you don't!</p>

    <p>
      Voting is done <strong>by participants only</strong>. But luckily, you can participate!
      So sign up today!
    </p>

    <h2>Do I have to watch/vote on every video</h2>
    <p>
      <strong>No</strong>. We really hope you do though: you put a lot of work into your
      project, right? I'm sure you want everyone to watch your presentation. Please give the
      other teams the same consideration.
    </p>

    <p>
      But if you don't watch a video, you don't have to vote on it. Doing so won't affect
      their score, or your own.
    </p>

    <h2>What's a "compensation vote"?</h2>
    <p>
      We're all colleagues here, and know nobody is trying to game the system. However, if
      anyone <i>did</i> try to game it, this would prevent that. See
      <a
        href="https://github.com/acv-auctions/hackathon/blob/main/docs/judging.md#vote-compensation"
      >
        GitHub
      </a>
      for a more detailed explanation.
    </p>

    <h2>Can I change my vote?</h2>
    <p>Yep! You can change them until the end of <strong>Judging</strong>.</p>

    <!-- <h2>How do I make a video?</h2> -->
    <h2>What is the criteria for my video?</h2>
    <p>
      It has to be within the time limit set for each event: this changes each time, so keep
      an eye out for an announcement from your admins. It will also be displayed on the
      event page.
    </p>

    <p>
      If a video is longer than allowed, <strong>stop watching at the time limit</strong>. It's
      not fair if you edited yourself down and another team ignored the rules!
    </p>

    <p>
      You also need to clearly state <strong>what you did beforehand</strong>, and which
      libraries you used. There's a big difference between "I put a coat of polish on something
      I've been working on for five years" and "I created this giant project from scratch",
      just like there's a difference between "I broke the laws of physics" vs "I typed
      <a
        href="https://xkcd.com/353/"
      >
        import antigravity
      </a>
      ".
    </p>

    <h2>Can I speed up the video to make it fit within the limit?</h2>
    <p>
      ...yes, <i>but</i> keep in mind that some people may find it offputting, or even be
      unable to understand what you're saying if their primary language is different from
      yours. This could lead to lower scores!
    </p>

    <h2>How do I submit a video?</h2>
    <p>The team leader can add it on the team information page (click on the edit icon)</p>

    <h2>What are the deadlines in each state?</h2>
    <p>Teams can only <i>be created</i> while in <strong>Registration</strong>.</p>

    <p>
      You can <i>join, leave, and switch teams</i> while in <strong>Hacking</strong>
      (but no more teams can be created!)
    </p>

    <p>
      You <strong>must</strong> <i>create and submit a video</i> before the end of
      <strong>Hacking</strong>. Any team that doesn't have a video <i>will be removed</i>.
    </p>

    <p>
      You can <i>vote for other teams</i> while in <strong>Judging</strong>.
      <strong>Leaving a team is permanent, and is resigning from the event!</strong>
    </p>

    <p>
      You can <i>redeem giveaways</i> (where appropriate)
      until an event is <strong>Completed</strong>.
    </p>

    <h2>I have other questions</h2>
    <p>
      Reach out to the event admins! Failing that, or if an event isn't going on right now, to the
      <RouterLink to="/committee">
        Committee
      </RouterLink>
      .
    </p>
  </div>
</template>

<script>
export default {
  name: 'FAQ',
};
</script>
